import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { LogOut, ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import tiltLogo from "@/assets/tilt-logo.jpg";
import { SmartEssayWordmark } from "@/components/brand";

const navLinks = [
  { to: "/help", label: "How it works" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
] as const;

const hubLinks = [
  { to: "/app", label: "Hub" },
  { to: "/history", label: "Past outlines" },
  { to: "/account", label: "Account" },
  { to: "/pricing", label: "Plans" },
] as const;

export function SiteHeader() {
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setUserId(data.session?.user?.id ?? null));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setUserId(session?.user?.id ?? null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isLanding = pathname === "/";
  const isAppArea = ["/app", "/generator", "/reviewer", "/tutor", "/history", "/account"].some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
  const showAuthCtas = isLanding && !userId;
  const showSignOut = !!userId;

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/" });
  };

  return (
    <header className="relative z-40 border-b border-border bg-background">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-x-6 gap-y-2 px-4 py-3 sm:px-6">
        <Link to="/" className="flex items-center gap-2.5 shrink-0">
          <img src={tiltLogo} alt="Tilt.University" className="h-9 w-9 rounded-md object-cover" />
          <SmartEssayWordmark className="hidden sm:inline" />
        </Link>

        <nav className="order-3 flex w-full flex-wrap items-center justify-end gap-x-4 gap-y-1 text-sm font-medium text-muted-foreground sm:order-2 sm:ml-auto sm:w-auto lg:gap-x-6">
          {isAppArea
            ? hubLinks.map((l) => (
                <Link key={l.to} to={l.to} className="transition-colors hover:text-foreground">
                  {l.label}
                </Link>
              ))
            : navLinks.map((l) => (
                <Link key={l.to} to={l.to} className="transition-colors hover:text-foreground">
                  {l.label}
                </Link>
              ))}
          <DropdownMenu>
            <DropdownMenuTrigger className="group flex items-center gap-0.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus:outline-none">
              Products
              <ChevronDown className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180" aria-hidden />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[11rem]">
              <DropdownMenuItem asChild>
                <a href="https://tilt.university" target="_blank" rel="noopener noreferrer" className="cursor-pointer text-sm font-medium">
                  Tilt.University
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a href="https://smart-reader.ai" target="_blank" rel="noopener noreferrer" className="cursor-pointer text-sm font-medium">
                  Smart-Reader.ai
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a href="https://smartteacher.ai" target="_blank" rel="noopener noreferrer" className="cursor-pointer text-sm font-medium">
                  SmartTeacher.ai
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/" className="cursor-pointer text-sm font-medium text-primary">
                  SmartEssay.ai
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div className="order-2 flex items-center gap-2 sm:order-3">
          {showAuthCtas && (
            <>
              <Button asChild size="sm" variant="ghost" className="rounded-full font-medium">
                <Link to="/auth">Sign in</Link>
              </Button>
              <Button
                asChild
                size="sm"
                className="rounded-full bg-primary px-5 font-medium text-primary-foreground hover:bg-primary/90"
              >
                <Link to="/auth">Start free</Link>
              </Button>
            </>
          )}
          {showSignOut && (
            <Button size="sm" variant="ghost" className="rounded-full font-medium" onClick={handleSignOut}>
              <LogOut className="mr-1.5 h-3.5 w-3.5" /> Sign out
            </Button>
          )}
        </div>
      </div>

      <div className="border-t border-border bg-muted/40">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-2 gap-y-1 px-4 py-2 text-center text-xs font-medium text-muted-foreground sm:text-sm">
          <span>A Tilt.University product — teacher-built AI writing tools.</span>
          <a href="https://tilt.university" target="_blank" rel="noopener noreferrer" className="font-semibold text-primary underline-offset-4 hover:underline">
            Explore the ecosystem →
          </a>
        </div>
      </div>
    </header>
  );
}
